import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ADD_NEW = '__add_new__';

export default function ClinicianSelect({ value, setup, setSetup, onSelect, disabled, testId, filterNames }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const all = (setup?.clinicians || []).filter(Boolean).sort();
  const clinicians = Array.isArray(filterNames) ? all.filter(n => filterNames.includes(n)) : all;

  const handleChange = (v) => {
    if (v === ADD_NEW) {
      setOpen(true);
      return;
    }
    onSelect(v);
  };

  const addClinician = async () => {
    const clean = name.trim();
    if (!clean) return toast.error('Enter a name');
    if (clinicians.includes(clean)) {
      toast.info('Already in list');
      onSelect(clean);
      setOpen(false);
      setName('');
      return;
    }
    const next = { ...(setup || {}), clinicians: [...clinicians, clean] };
    try {
      await api.put('/setup', { clinicians: next.clinicians });
      setSetup(next);
      onSelect(clean);
      toast.success(`Added ${clean}`);
      setOpen(false);
      setName('');
    } catch { toast.error('Failed to add clinician'); }
  };

  return (
    <>
      <Select value={value || ''} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className="rounded-xl mt-1" data-testid={testId}><SelectValue placeholder="Select clinician…" /></SelectTrigger>
        <SelectContent>
          {clinicians.length === 0 && <SelectItem value="__none__" disabled>{Array.isArray(filterNames) ? 'No clinicians assigned to this clinic' : 'No clinicians yet'}</SelectItem>}
          {clinicians.map((c, idx) => <SelectItem key={`${c}-${idx}`} value={c}>{c}</SelectItem>)}
          {!disabled && (
            <SelectItem value={ADD_NEW} className="text-[#685B72] font-semibold border-t border-[#E8E5EC] mt-1 pt-2">
              <span className="flex items-center gap-2"><Plus size={13} /> Add new clinician…</span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="add-clinician-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Add clinician</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs text-[#685B72]">Clinician name</Label>
            <Input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addClinician()}
              placeholder="e.g. Ashley Rioux, M.S., CCC-SLP"
              className="rounded-xl"
              data-testid="new-clinician-name"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setName(''); }}>Cancel</Button>
            <Button onClick={addClinician} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid="new-clinician-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
